import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardOverview, getRecentActivity, getExpiringBlocks, getProjects } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Building2, 
  Home, 
  Lock, 
  CheckCircle2, 
  Clock, 
  TrendingUp, 
  AlertTriangle,
  Activity,
  ArrowUpRight,
  IndianRupee,
  RefreshCw
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [expiringBlocks, setExpiringBlocks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [statsData, activityData, blocksData, projectsData] = await Promise.all([
        getDashboardOverview(),
        getRecentActivity(10),
        getExpiringBlocks(24),
        getProjects()
      ]);
      setStats(statsData);
      setActivity(activityData);
      setExpiringBlocks(blocksData);
      setProjects(projectsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const getActionIcon = (action) => {
    if (action.includes('block')) return <Lock className="w-4 h-4" />;
    if (action.includes('book')) return <CheckCircle2 className="w-4 h-4" />;
    if (action.includes('create')) return <Building2 className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const getActionColor = (action) => {
    if (action.includes('block')) return 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400';
    if (action.includes('book')) return 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-400';
    if (action.includes('available')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400';
    return 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Overview</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.name}. Here&apos;s your inventory at a glance.
          </p>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-dashboard"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="hover-lift hover-glow" data-testid="stat-total-units">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Units</p>
                <p className="text-3xl font-bold mt-2">{stats?.total_units || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Across {stats?.total_projects || 0} projects
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Home className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift hover-glow" data-testid="stat-available">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Available</p>
                <p className="text-3xl font-bold mt-2 text-emerald-600 dark:text-emerald-400">
                  {stats?.available_units || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.total_units ? ((stats.available_units / stats.total_units) * 100).toFixed(1) : 0}% of inventory
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift hover-glow" data-testid="stat-blocked">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Soft Blocked</p>
                <p className="text-3xl font-bold mt-2 text-amber-600 dark:text-amber-400">
                  {stats?.soft_blocked_units || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats?.expiring_blocks || 0} expiring in 24h
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift hover-glow" data-testid="stat-booked">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Booked</p>
                <p className="text-3xl font-bold mt-2 text-rose-600 dark:text-rose-400">
                  {stats?.booked_units || 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {formatCurrency(stats?.booked_value || 0)} value
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-rose-100 dark:bg-rose-950 flex items-center justify-center">
                <Lock className="w-6 h-6 text-rose-600 dark:text-rose-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Value Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="hover-lift hover-glow" data-testid="stat-inventory-value">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Inventory Value</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(stats?.total_inventory_value || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <IndianRupee className="w-6 h-6 text-primary" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-600">
                {stats?.booked_value ? ((stats.booked_value / stats.total_inventory_value) * 100).toFixed(1) : 0}% sold
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="hover-lift hover-glow" data-testid="stat-booked-value">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Booked Value</p>
                <p className="text-3xl font-bold mt-2">{formatCurrency(stats?.booked_value || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-950 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {stats?.booked_units || 0} units confirmed
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sales & Occupancy Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card data-testid="sales-status-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Sales Inventory</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: 'Available', value: stats?.available_units || 0, color: 'text-emerald-600 dark:text-emerald-400' },
                { label: 'Soft Blocked', value: stats?.soft_blocked_units || 0, color: 'text-amber-600 dark:text-amber-400' },
                { label: 'Booked', value: stats?.booked_units || 0, color: 'text-rose-600 dark:text-rose-400' },
                { label: 'Sold', value: stats?.sold_units || 0, color: 'text-blue-600 dark:text-blue-400' },
                { label: 'On Hold', value: stats?.on_hold_units || 0, color: 'text-zinc-500' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="occupancy-status-summary">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold">Occupancy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Vacant', value: stats?.vacant_units || 0, color: 'text-emerald-600 dark:text-emerald-400' },
                { label: 'Rented', value: stats?.rented_units || 0, color: 'text-violet-600 dark:text-violet-400' },
                { label: 'Owner Occupied', value: stats?.owner_occupied_units || 0, color: 'text-blue-600 dark:text-blue-400' },
              ].map(s => (
                <div key={s.label} className="text-center">
                  <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Expiring Blocks */}
        <Card className="lg:col-span-1" data-testid="expiring-blocks-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Expiring Soon
              </CardTitle>
              <Badge variant="secondary">{expiringBlocks.length}</Badge>
            </div>
            <CardDescription>Soft blocks expiring in 24 hours</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {expiringBlocks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No expiring blocks</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {expiringBlocks.map((unit) => (
                    <div 
                      key={unit.unit_id} 
                      className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-950/50 border border-amber-200 dark:border-amber-900"
                    >
                      <div>
                        <p className="font-medium text-sm">{unit.unit_number}</p>
                        <p className="text-xs text-muted-foreground">{unit.configuration}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                          {unit.block_expiry && formatDistanceToNow(new Date(unit.block_expiry), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="lg:col-span-2" data-testid="recent-activity-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Recent Activity
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <a href="/audit">
                  View All <ArrowUpRight className="w-4 h-4 ml-1" />
                </a>
              </Button>
            </div>
            <CardDescription>Latest actions across all projects</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[280px]">
              {activity.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No recent activity</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {activity.map((log) => (
                    <div 
                      key={log.log_id} 
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {log.user_name} <span className="text-muted-foreground font-normal">{log.action.replace(/_/g, ' ')}</span>
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {log.details || `${log.entity_type}: ${log.entity_id}`}
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDistanceToNow(new Date(log.timestamp), { addSuffix: true })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      {/* Projects Quick View */}
      {projects.length > 0 && (
        <Card data-testid="projects-quick-view">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Your Projects
              </CardTitle>
              <Button variant="ghost" size="sm" asChild>
                <a href="/projects">
                  Manage <ArrowUpRight className="w-4 h-4 ml-1" />
                </a>
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.slice(0, 6).map((project) => (
                <div 
                  key={project.project_id}
                  className="p-4 rounded-lg border border-border hover:border-primary/50 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold">{project.name}</h3>
                      <p className="text-sm text-muted-foreground">{project.location || project.code}</p>
                    </div>
                    <Badge variant="secondary">{project.total_units} units</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
